"use client";

import { Card, Skeleton } from "antd"
import CalendarIcon from "@/components/icons/CalendarIcon"
import { searchFlightDrawers } from "../../FormSearch/mobile/DrawerFormSearchFlightWrapper"
import Carousel from "../../../common/Carousel"
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next"
import { getFlightsAllAirlines } from "@/utils/flight/getFlightsAllAirlines";
import { flightActions } from "@/lib/redux/features/flight/reduce";
import { storeRef } from "../../../providers/StoreProvider";
import { useTETranslation } from "@/hooks/app/useTETranslation";

type MinFareItem = {
  date: string;
  lowestFare: number;
  lowestPriceTotal: number;
}

type WeeklyDisplayItem = {
  dateKey: string;
  displayDate: string;
  minFare?: MinFareItem;
}

/** How many date chips fill one desktop viewport (evenly). */
const VISIBLE_DAYS_DESKTOP = 7
const VISIBLE_DAYS_MOBILE = 4
/** Total chips in the strip — enough to page without empty trailing space. */
const TOTAL_DAYS = 14

/** dayjs().day(): 0 = CN … 6 = Thứ 7 */
const VI_WEEKDAY_LABELS = ["CN", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"] as const

const formatChipDate = (date: dayjs.Dayjs) => {
  const weekday = VI_WEEKDAY_LABELS[date.day()]
  return `${weekday}, ${date.format("DD")} Th${date.format("MM")}`
}

/** Normalize API date (ISO, YYYY-MM-DD, YYYYMMDD) to YYYY-MM-DD. */
const toDateKey = (raw?: string) => {
  if (!raw) return ""
  const parsed = dayjs(raw)
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD")
  const compact = String(raw).replace(/[^\d]/g, "")
  if (compact.length >= 8) {
    return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`
  }
  return ""
}

const getPriceByMode = (fare: MinFareItem, mode: string): number => {
  return mode === "basic_1_adult" ? fare.lowestFare : fare.lowestPriceTotal
}

type FilterFlightsByDateProps = {
  /** Sticky compact header on desktop results */
  variant?: "default" | "sticky"
}

const FilterFlightsByDate = ({ variant = "default" }: FilterFlightsByDateProps) => {
  const searchMin = useAppSelector(state => state.flight.search_min_flights_v1)
  const selectedFlightInput = useAppSelector(state => state.flight.inputFlights[state.flight.selectedFligthInputIndex])
  const minFarePrice = useAppSelector(state => state.flight.flightsResult.minFarePrice)
  const minTotalFarePrice = useAppSelector(state => state.flight.flightsResult.minTotalFarePrice)
  const currentModeShowPrice = useAppSelector(state => state.flight.currentModeShowPrice)
  const isDesktop = useIsDesktop()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { getFormatCurrency } = useTETranslation()
  const selectedDate = dayjs(selectedFlightInput?.dateFrom)
  const routeKey = `${selectedFlightInput?.fromAirport?.code || ""}${selectedFlightInput?.toAirport?.code || ""}`
  const routeFallbackPrice = currentModeShowPrice === 'basic_1_adult'
    ? minFarePrice[routeKey] || 0
    : minTotalFarePrice[routeKey] || 0

  const weeklyDisplayData = useMemo<WeeklyDisplayItem[]>(() => {
    const raw = searchMin.data as MinFareItem[] | { listMinFare?: MinFareItem[] } | undefined
    const listMinFare = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.listMinFare)
        ? raw.listMinFare
        : []
    const mappingByDate = listMinFare.reduce<Record<string, MinFareItem>>((acc, item) => {
      const fareRow = item as MinFareItem & { flightDate?: string; departDate?: string }
      const dateKey = toDateKey(fareRow.date || fareRow.flightDate || fareRow.departDate)
      if (dateKey) acc[dateKey] = item
      return acc
    }, {})
    const startDate = selectedDate.isValid() ? selectedDate : dayjs()

    return Array.from({ length: TOTAL_DAYS }, (_, index) => {
      const date = startDate.add(index, "day")
      const dateKey = date.format("YYYY-MM-DD")
      return {
        dateKey,
        displayDate: formatChipDate(date),
        minFare: mappingByDate[dateKey]
      }
    })
  }, [searchMin.data, selectedDate])

  const lowestPriceInStrip = useMemo(() => {
    let lowest: number | null = null
    weeklyDisplayData.forEach((item, index) => {
      const isSelectedDay =
        selectedDate.isValid() && selectedDate.format("YYYY-MM-DD") === item.dateKey
      const fallbackPrice = isSelectedDay || index === 0 ? routeFallbackPrice : 0
      const farePrice = item.minFare
        ? (getPriceByMode(item.minFare, currentModeShowPrice) || item.minFare.lowestFare || 0)
        : 0
      const price = farePrice || fallbackPrice
      if (price > 0 && (lowest === null || price < lowest)) {
        lowest = price
      }
    })
    return lowest
  }, [weeklyDisplayData, routeFallbackPrice, currentModeShowPrice, selectedDate])

  const isSticky = variant === "sticky"
  const visibleDays = isSticky
    ? 5
    : isDesktop
      ? VISIBLE_DAYS_DESKTOP
      : VISIBLE_DAYS_MOBILE

  const onClick = async (date: string) => {
    const inputFlights = storeRef.current?.getState().flight.inputFlights
    const indexFlight = (inputFlights || []).findIndex(input =>
      `${selectedFlightInput.fromAirport?.code}${selectedFlightInput.toAirport?.code}` === `${input.fromAirport?.code}${input.toAirport?.code}`
    )
    if (indexFlight > -1) {
      dispatch(flightActions.changeDateFrom({ indexInputFlight: indexFlight, dateFrom: date }))
      await getFlightsAllAirlines({})
    }
  }
  const renderDateChip = (
    item: WeeklyDisplayItem,
    index: number,
  ) => {
    const isActive =
      selectedDate.isValid() && selectedDate.format("YYYY-MM-DD") === item.dateKey
    const fallbackPrice = isActive || index === 0 ? routeFallbackPrice : 0
    const farePrice = item.minFare
      ? (getPriceByMode(item.minFare, currentModeShowPrice) || item.minFare.lowestFare || 0)
      : 0
    const itemPrice = farePrice || fallbackPrice || null
    const hasPrice = itemPrice !== null && itemPrice > 0
    const isLowest =
      hasPrice &&
      lowestPriceInStrip !== null &&
      itemPrice === lowestPriceInStrip

    return (
      <button
        type="button"
        key={item.dateKey}
        onClick={() => onClick(item.dateKey)}
        className={[
          "flights__list--filterbd-chip",
          isActive && "flights__list--filterbd-chip--active",
          !hasPrice && "flights__list--filterbd-chip--empty",
          isLowest && !isActive && "flights__list--filterbd-chip--lowest",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span className="flights__list--filterbd-chip-date">{item.displayDate}</span>
        <span className="flights__list--filterbd-chip-price">
          {hasPrice ? getFormatCurrency(itemPrice) : t("flight.see_price")}
        </span>
      </button>
    )
  }

  const dateStrip = (
    <div className={`flights__list--filterbd-strip${isSticky ? " flights__list--filterbd-strip--sticky" : ""}`}>
      <div className="flights__list--filterbd-carousel-wrap">
        <Carousel
          key={`${routeKey}-${selectedDate.isValid() ? selectedDate.format("YYYY-MM-DD") : "x"}-${searchMin.loading ? "loading" : "ready"}-${variant}`}
          className="carousel-main flights__list--filterbd-carousel"
          options={{
            pageDots: false,
            cellAlign: "left",
            contain: true,
            draggable: true,
            groupCells: visibleDays,
            prevNextButtons: !!isDesktop,
            percentPosition: true,
          }}
        >
          {searchMin.loading
            ? Array(TOTAL_DAYS)
                .fill(0)
                .map((_, index) => (
                  <div key={index} className="flights__list--filterbd-chip flights__list--filterbd-chip--skeleton">
                    <Skeleton active title={false} paragraph={{ rows: 2 }} />
                  </div>
                ))
            : weeklyDisplayData.map((item, index) => renderDateChip(item, index))}
        </Carousel>
      </div>
      {!isDesktop && !isSticky && (
        <button
          type="button"
          className="flights__list--filterbd-btncal"
          aria-label="Open calendar"
          onClick={() => searchFlightDrawers.selectDate?.open()}
        >
          <CalendarIcon />
        </button>
      )}
    </div>
  )

  if (!isDesktop || isSticky) {
    return dateStrip
  }

  return (
    <Card
      size="small"
      className="flights__list--filterbd"
      classNames={{
        body: "flights__list--filterbd-body",
      }}
      variant="outlined"
    >
      {dateStrip}
    </Card>
  )
}

export default FilterFlightsByDate
